import React, { useId, useState } from 'react'
import ArtworkQuantityInput from './ArtworkQuantityInput'
import DpiBadge from './DpiBadge'
import GuidedSizePresets, { dimensionsForPreset } from './GuidedSizePresets'
import {
  calculateDpi,
  getDpiQuality,
  getOversizedArtworkWarningCopy,
  isArtworkOversized,
  recommendedPrintDimensions,
} from './artworkQuality'

function positiveNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

export default function GuidedArtworkSizeGroup({
  image,
  imageUrl,
  config,
  groupNumber,
  groupCount,
  arrangementSheetSize,
  disabled = false,
  onChange,
  onRemove,
}) {
  const [aspectLocked, setAspectLocked] = useState(true)
  const aspectHelpId = useId()
  const sourceName = image.originalFilename || 'Artwork'
  const controlName = groupCount > 1 ? `${sourceName}, size ${groupNumber}` : sourceName
  const width = config.printWidthIn ?? ''
  const height = config.printHeightIn ?? ''
  const ratio = image.widthPx && image.heightPx ? image.heightPx / image.widthPx : null
  const oversized = !disabled && isArtworkOversized(width, height, arrangementSheetSize)
  const recommendedSize = recommendedPrintDimensions(image.widthPx, image.heightPx)
  const currentDpi = calculateDpi(image.widthPx, image.heightPx, width, height)
  const resolutionNeedsAttention = ['caution', 'poor'].includes(getDpiQuality(currentDpi))

  const updateWidth = event => {
    const nextWidth = positiveNumber(event.target.value)
    onChange?.({
      printWidthIn: nextWidth,
      printHeightIn: aspectLocked && ratio && nextWidth
        ? Number((nextWidth * ratio).toFixed(2))
        : config.printHeightIn,
    })
  }

  const updateHeight = event => {
    const nextHeight = positiveNumber(event.target.value)
    onChange?.({
      printWidthIn: aspectLocked && ratio && nextHeight
        ? Number((nextHeight / ratio).toFixed(2))
        : config.printWidthIn,
      printHeightIn: nextHeight,
    })
  }

  const selectPreset = preset => {
    const dimensions = dimensionsForPreset({ image, config, longerSideIn: preset.longerSideIn })
    if (dimensions) onChange?.(dimensions)
  }

  return (
    <section className="guided-size-group" data-testid={`guided-size-group-${groupNumber}`}>
      <div className="guided-size-group__heading">
        <div>
          <strong>Size {groupNumber}</strong>
          <span>{config.quantity} {config.quantity === 1 ? 'copy' : 'copies'} at {width || '—'}&quot; × {height || '—'}&quot;</span>
        </div>
        {onRemove && (
          <button
            type="button"
            className="guided-size-group__remove"
            aria-label={`Remove size ${groupNumber} for ${sourceName}`}
            onClick={onRemove}
          >
            Remove size
          </button>
        )}
      </div>

      <GuidedSizePresets
        image={image}
        imageUrl={imageUrl}
        config={config}
        disabled={disabled}
        description="Choose a common size or enter custom dimensions below."
        accessibleName={controlName}
        onSelect={selectPreset}
      />

      <div className="guided-source-card__controls">
        <label>
          <span>Print width</span>
          <span className="guided-source-card__input-with-unit">
            <input
              aria-label={`Print width for ${controlName}`}
              data-guided-source-focus-target={groupNumber === 1 ? true : undefined}
              type="number"
              min="0.01"
              step="0.01"
              value={width}
              disabled={disabled}
              onFocus={event => event.currentTarget.select()}
              onChange={updateWidth}
            />
            <span>in</span>
          </span>
        </label>
        <button
          className={`guided-source-card__aspect${aspectLocked ? ' guided-source-card__aspect--locked' : ''}`}
          type="button"
          aria-pressed={aspectLocked}
          aria-label={`${aspectLocked ? 'Unlock' : 'Lock'} width and height for ${controlName}`}
          aria-describedby={aspectHelpId}
          disabled={disabled}
          onClick={() => setAspectLocked(value => !value)}
        >
          Keep artwork shape: {aspectLocked ? 'On' : 'Off'}
        </button>
        <label>
          <span>Print height</span>
          <span className="guided-source-card__input-with-unit">
            <input
              aria-label={`Print height for ${controlName}`}
              type="number"
              min="0.01"
              step="0.01"
              value={height}
              disabled={disabled}
              onFocus={event => event.currentTarget.select()}
              onChange={updateHeight}
            />
            <span>in</span>
          </span>
        </label>
        <label className="guided-source-card__quantity">
          <span>Quantity</span>
          <span className="guided-source-card__quantity-stepper">
            <button
              type="button"
              aria-label={`Decrease requested quantity for ${controlName}`}
              disabled={disabled || config.quantity <= 1}
              onClick={() => onChange?.({ quantity: config.quantity - 1, included: true })}
            >
              <span aria-hidden="true">−</span>
            </button>
            <ArtworkQuantityInput
              aria-label={`Requested quantity for ${controlName}`}
              inputMode="numeric"
              min={1}
              value={config.quantity}
              disabled={disabled}
              onCommit={quantity => onChange?.({ quantity, included: true })}
            />
            <button
              type="button"
              aria-label={`Increase requested quantity for ${controlName}`}
              disabled={disabled || config.quantity >= 99}
              onClick={() => onChange?.({ quantity: config.quantity + 1, included: true })}
            >
              <span aria-hidden="true">+</span>
            </button>
          </span>
        </label>
        <p id={aspectHelpId} className="guided-source-card__aspect-help">
          {aspectLocked
            ? 'Recommended: changing width or height adjusts the other automatically, so the artwork does not stretch.'
            : 'Width and height change separately and may stretch the artwork.'}
        </p>
      </div>

      {!disabled && (
        <div className="guided-source-card__quality">
          <DpiBadge
            widthPx={image.widthPx}
            heightPx={image.heightPx}
            printWidthIn={width}
            printHeightIn={height}
          />
          {resolutionNeedsAttention && recommendedSize && (
            <button
              className="artwork-quality__recommended-action"
              type="button"
              onClick={() => onChange?.({
                printWidthIn: recommendedSize.widthIn,
                printHeightIn: recommendedSize.heightIn,
              })}
            >
              Resize to 300 DPI ({recommendedSize.widthIn}&quot; × {recommendedSize.heightIn}&quot;)
            </button>
          )}
          {oversized && (
            <p className="guided-source-card__warning" role="alert">
              {getOversizedArtworkWarningCopy(width, height, arrangementSheetSize)}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
